import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'

// Anchor registry for the spotlight tour. Target components call
// useTourAnchor(key) — they get back a ref to spread on the DOM
// node that should be highlighted. The context holds a Map<key,
// element> the tour controller reads at step time.
//
// Using a ref-based registry (not state) so registering an anchor
// doesn't re-render the consumer or the controller — the tour just
// reads the current map whenever a step becomes active.
const TourAnchorContext = createContext(null)

export function TourAnchorProvider({ children }) {
  const anchorsRef = useRef(new Map())

  const register = useCallback((key, element) => {
    if (!key) return
    if (element) anchorsRef.current.set(key, element)
    else anchorsRef.current.delete(key)
  }, [])

  const getAnchor = useCallback((key) => {
    if (!key) return null
    return anchorsRef.current.get(key) || null
  }, [])

  return (
    <TourAnchorContext.Provider value={{ register, getAnchor }}>
      {children}
    </TourAnchorContext.Provider>
  )
}

// Component-side helper. Spread the returned `ref` on the DOM node
// you want the tour to highlight. Re-registers automatically when
// the node remounts.
export function useTourAnchor(key) {
  const ctx = useContext(TourAnchorContext)
  const ref = useRef(null)

  // Effect-based register so the DOM element is committed by React
  // before we hand it off to the tour controller. Cleanup
  // unregisters on unmount.
  useEffect(() => {
    if (!ctx) return undefined
    ctx.register(key, ref.current)
    return () => ctx.register(key, null)
  }, [ctx, key])

  return ref
}

export function useTourAnchorRegistry() {
  const ctx = useContext(TourAnchorContext)
  if (!ctx) throw new Error('useTourAnchorRegistry must be used inside <TourAnchorProvider>')
  return ctx
}
